import { historicalProductName, historicalSku, historicalVariantOptions, hasLiveCatalogueIdentity, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { Fragment, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { firstRelation } from "@/lib/relations";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { ListSkeleton } from "@/components/base/ListSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { fmtDate, statusLabel, variantLabel } from "@/lib/format";
import * as transfersApi from "@/lib/api/transfers";
import { downloadCsv, toCsv } from "@/lib/csv";
import { useCreateDeepLink } from "@/hooks/useCreateDeepLink";

interface TransferRow {
  id: string;
  transfer_number: string;
  from_location_id: string;
  to_location_id: string;
  status: string;
  created_at: string;
}

interface TransferItem extends CatalogueHistoryFields { id: string; stock_transfer_id: string; product_id: string | null; variant_id: string | null; qty_sent: number; qty_received: number; products?: { name: string } | null; variants?: { sku: string | null; option_values: Record<string, string> | null } | null; }
interface Option { id: string; name: string; }
interface ProductOption { id: string; name: string; sku: string; }
interface DraftLine { uid: string; product_id: string; variant_id: string | null; qty: number; }
interface VariantOption { id: string; product_id: string; sku: string | null; option_values: Record<string, string> | null; }

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function Transfers() {
  const { org } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [createIdemKey, setCreateIdemKey] = useState("");

  useCreateDeepLink(() => { setError(""); setCreateIdemKey(crypto.randomUUID()); setOpen(true); });

  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["transfers", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const [transfers, items, locations, products, variants] = await Promise.all([
        supabase.from("stock_transfers").select("id, transfer_number, from_location_id, to_location_id, status, created_at").eq("org_id", org!.id).order("created_at", { ascending: false }),
        supabase.from("stock_transfer_items").select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, variant_attributes_snapshot, catalogue_snapshot_origin, id, stock_transfer_id, product_id, variant_id, qty_sent, qty_received, products(name), variants(sku, option_values)").eq("org_id", org!.id),
        supabase.from("locations").select("id, name").eq("org_id", org!.id).order("name"),
        supabase.from("products").select("id, name, sku").eq("org_id", org!.id).neq("status", "archived").order("name"),
        supabase.from("variants").select("id, product_id, sku, option_values").eq("org_id", org!.id).neq("status", "archived").order("position"),
      ]);
      return {
        transfers: (transfers.data ?? []) as TransferRow[],
        items: (items.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          variants: firstRelation(row.variants),
        })) as TransferItem[],
        locations: (locations.data ?? []) as Option[],
        products: (products.data ?? []) as ProductOption[],
        variants: (variants.data ?? []) as VariantOption[],
      };
    },
  });

  const itemsByTransfer = (data?.items ?? []).reduce<Record<string, TransferItem[]>>((acc, it) => {
    (acc[it.stock_transfer_id] = acc[it.stock_transfer_id] ?? []).push(it);
    return acc;
  }, {});

  const locName = (data?.locations ?? []).reduce<Record<string, string>>((acc, l) => {
    acc[l.id] = l.name;
    return acc;
  }, {});

  const filteredTransfers = useMemo(() => {
    if (!data) return [];
    return data.transfers.filter((t) => {
      if (statusFilter && t.status !== statusFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const num = t.transfer_number ?? "";
        const from = locName[t.from_location_id] ?? "";
        const to = locName[t.to_location_id] ?? "";
        if (!num.toLowerCase().includes(q) && !from.toLowerCase().includes(q) && !to.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [data, search, statusFilter, locName]);

  const statusOptions = useMemo(() => {
    const set = new Set((data?.transfers ?? []).map((t) => t.status));
    return Array.from(set).sort();
  }, [data]);

  const addLine = () => setLines((l) => [...l, { uid: crypto.randomUUID(), product_id: "", variant_id: null, qty: 1 }]);
  const updateLine = (idx: number, patch: Partial<DraftLine>) =>
    setLines((l) => l.map((line, i) => (i === idx ? { ...line, ...patch } : line)));
  const removeLine = (idx: number) => setLines((l) => l.filter((_, i) => i !== idx));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["transfers", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!fromId || !toId || fromId === toId || lines.length === 0) {
      setError("Pick two different locations and at least one line item.");
      return;
    }
    if (lines.some((l) => !l.product_id || Number(l.qty) <= 0)) {
      setError("Every line needs a product and a positive quantity.");
      return;
    }
    if (lines.some((l) => {
      const vs = (data?.variants ?? []).filter((v) => v.product_id === l.product_id);
      return vs.length > 1 && !l.variant_id;
    })) {
      setError("Select a variant for each variable product.");
      return;
    }
    setSaving(true);

    // One transactional call: header + every line commit or roll back together,
    // the number comes from a sequence (no client-side race), and the sellable
    // variant is resolved server-side. No half-created transfer can remain.
    const pLines = lines.map((l) => ({
      product_id: l.product_id,
      variant_id: l.variant_id ?? null,
      qty: Number(l.qty),
    }));

    try {
      await transfersApi.createStockTransfer({
        fromLocationId: fromId,
        toLocationId: toId,
        lines: pLines,
        idempotencyKey: createIdemKey,
      });
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }

    setSaving(false);

    setOpen(false);
    setFromId("");
    setToId("");
    setLines([]);
    invalidate();
  };

  const runAction = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setBusy(false);
      setError((e as Error).message);
      return;
    }
    setBusy(false);
    invalidate();
  };

  const handleExport = () => {
    const headers = ["transfer_number", "from", "to", "status", "created_at"];
    const rows = filteredTransfers.map((t) => [
      t.transfer_number,
      locName[t.from_location_id] ?? "",
      locName[t.to_location_id] ?? "",
      t.status,
      fmtDate(t.created_at),
    ]);
    downloadCsv(`transfers-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, rows));
  };

  if (isLoading) return <ListSkeleton />;

  return (
    <div>
      <PageHeader title="Transfers" subtitle="Move inventory between locations">
        <button onClick={handleExport} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-download-2-line" /> Export
        </button>
        <button onClick={() => { setError(""); setCreateIdemKey(crypto.randomUUID()); setOpen(true); }} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-add-line" />
          New Transfer
        </button>
      </PageHeader>

      {error && <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

      {isError ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load transfers.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      ) : !data || data.transfers.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200">
          <EmptyState title="No transfers" description="Create a transfer to move stock between locations." />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by transfer or location..."
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              <option value="">All statuses</option>
              {statusOptions.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </select>
            <span className="inline-flex items-center text-xs text-slate-400 whitespace-nowrap">{filteredTransfers.length} of {data?.transfers.length ?? 0}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Transfer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">From</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">To</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Created</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransfers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">No transfers match your search or filter.</td>
                  </tr>
                )}
                {filteredTransfers.map((t) => {
                  const items = itemsByTransfer[t.id] ?? [];
                  const isOpen = expanded === t.id;
                  return (
                    <Fragment key={t.id}>
                      <tr className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => setExpanded(isOpen ? null : t.id)}>
                        <td className="px-4 py-3 text-sm font-semibold text-slate-800 font-mono">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/transfers/${t.id}`); }}
                            className="hover:text-emerald-700 whitespace-nowrap"
                          >
                            {t.transfer_number}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{locName[t.from_location_id] ?? "—"}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{locName[t.to_location_id] ?? "—"}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{fmtDate(t.created_at)}</td>
                        <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {t.status === "draft" && (
                              <button
                                onClick={(e) => { e.stopPropagation(); runAction(() => transfersApi.shipStockTransfer(t.id)); }}
                                disabled={busy}
                                className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg whitespace-nowrap disabled:opacity-50"
                              >
                                Ship
                              </button>
                            )}
                            {t.status === "in_transit" && (
                              <button
                                onClick={(e) => { e.stopPropagation(); runAction(() => transfersApi.receiveStockTransfer(t.id, [])); }}
                                disabled={busy}
                                className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg whitespace-nowrap disabled:opacity-50"
                              >
                                Receive
                              </button>
                            )}
                            <i className={`ri-arrow-down-s-line text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-slate-50 bg-slate-50/40">
                          <td colSpan={6} className="px-4 py-3">
                            <div className="divide-y divide-slate-100">
                              {items.map((it) => (
                                <div key={it.id} className="py-2 flex items-center justify-between text-sm">
                                  <span className="font-medium text-slate-800">
                                    {historicalProductName(it)}
                                    {(variantLabel(historicalVariantOptions(it)) || historicalSku(it)) && (
                                      <span className="text-slate-500 font-normal ml-1">· {variantLabel(historicalVariantOptions(it)) || historicalSku(it)}</span>
                                    )}
                                  </span>
                                  <span className="text-slate-600">{it.qty_received} / {it.qty_sent} received</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Transfer" wide
        footer={
          <>
            <button onClick={() => setOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleCreate} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Creating..." : "Create Transfer"}
            </button>
          </>
        }
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>From location *</label>
              <select required value={fromId} onChange={(e) => setFromId(e.target.value)} className={inputCls}>
                <option value="">Select...</option>
                {data?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>To location *</label>
              <select required value={toId} onChange={(e) => setToId(e.target.value)} className={inputCls}>
                <option value="">Select...</option>
                {data?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-slate-500">Items to transfer</label>
              <button type="button" onClick={addLine} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap">
                <i className="ri-add-line" /> Add line
              </button>
            </div>
            {lines.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">No items yet.</div>
            ) : (
              <div className="space-y-2">
                {lines.map((l, idx) => {
                  const lineVariants = (data?.variants ?? []).filter((v) => v.product_id === l.product_id);
                  return (
                  <div key={l.uid} className="flex items-center gap-2">
                    <select value={l.product_id} onChange={(e) => { const vs = (data?.variants ?? []).filter((v) => v.product_id === e.target.value); updateLine(idx, { product_id: e.target.value, variant_id: vs.length === 1 ? vs[0].id : null }); }} className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
                      <option value="">Select product...</option>
                      {data?.products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                    </select>
                    {lineVariants.length === 1 ? (
                      <span className="w-32 px-3 py-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg truncate whitespace-nowrap">
                        {variantLabel(lineVariants[0].option_values) || lineVariants[0].sku || "Default"}
                      </span>
                    ) : lineVariants.length > 1 ? (
                      <select
                        value={l.variant_id ?? ""}
                        onChange={(e) => updateLine(idx, { variant_id: e.target.value || null })}
                        className="w-32 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                      >
                        <option value="">Select variant…</option>
                        {lineVariants.map((v) => <option key={v.id} value={v.id}>{variantLabel(v.option_values) || v.sku}</option>)}
                      </select>
                    ) : null}
                    <input type="number" min="1" value={l.qty} onChange={(e) => updateLine(idx, { qty: Number(e.target.value) })} className="w-20 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" placeholder="Qty" />
                    <button type="button" onClick={() => removeLine(idx)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
}